import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import hrPlusService from '../../services/hrPlusService';
import { PageHeader, headerCellSx, bodyRowSx, TableEmptyState } from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { useChartTokens, axisProps, gridProps, tooltipStyle, MAX_SERIES } from '../../dashboard/chartTokens';

// ---------------------------------------------------------------------------
// Pure formatting / shape-inference helpers
// ---------------------------------------------------------------------------

const isNum = (v: any) => v !== null && v !== undefined && v !== '' && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Number(v).toFixed(1)}%` : '—');
const fmtInt = (v: any) => (isNum(v) ? Number(v).toLocaleString('en-IN') : '—');

const humanize = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

const isMoneyKey = (key: string) => /cost|salary|payroll|amount|spend|expense|ctc/i.test(key);
const isPctKey = (key: string) => /pct|percent|rate|ratio/i.test(key);

const formatByKey = (key: string, value: any): string => {
  if (!isNum(value)) return value === null || value === undefined || value === '' ? '—' : String(value);
  if (isMoneyKey(key)) return fmtMoney(value);
  if (isPctKey(key)) return fmtPct(value);
  return fmtInt(value);
};

/** Every analytics row shape varies by endpoint — infer a label field (first
 * string field) and up to MAX_SERIES numeric value fields from the first row
 * rather than assuming fixed field names. */
const inferKeys = (rows: any[]): { labelKey: string | null; valueKeys: string[] } => {
  if (!rows || !rows.length) return { labelKey: null, valueKeys: [] };
  const sample = rows[0] || {};
  const keys = Object.keys(sample);
  const labelKey = keys.find((k) => typeof sample[k] === 'string') || keys[0] || null;
  const valueKeys = keys.filter((k) => k !== labelKey && isNum(sample[k])).slice(0, MAX_SERIES);
  return { labelKey, valueKeys };
};

/** True when the label field looks like a time period (month/date/week) rather
 * than a fixed category (department, bucket, stage, gender...). */
const looksLikeTimeSeries = (labelKey: string | null, rows: any[]): boolean => {
  if (!labelKey) return false;
  if (/month|period|date|week|day/i.test(labelKey)) return true;
  const sample = rows[0]?.[labelKey];
  return typeof sample === 'string' && /^\d{4}-\d{2}(-\d{2})?$/.test(sample);
};

const fmtPeriodLabel = (v: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(v || '');
  if (!m) return v;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

// ---------------------------------------------------------------------------
// Section shell — matches pageKit's TableSurface bordered-card treatment.
// ---------------------------------------------------------------------------

const SectionCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, height: '100%' }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: subtitle ? 0.25 : 1.5 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

// ---------------------------------------------------------------------------
// A single analytics panel: chart (auto time-series vs categorical) + a
// small table of the underlying rows, formatted defensively by field name.
// ---------------------------------------------------------------------------

const AnalyticsPanel: React.FC<{ title: string; subtitle?: string; rows: any[]; asDonut?: boolean }> = ({
  title,
  subtitle,
  rows,
  asDonut,
}) => {
  const T = useChartTokens();
  const { labelKey, valueKeys } = useMemo(() => inferKeys(rows), [rows]);
  const timeSeries = useMemo(() => looksLikeTimeSeries(labelKey, rows), [labelKey, rows]);
  const tableRows = useMemo(() => (timeSeries ? rows.slice(-12) : rows), [rows, timeSeries]);
  const chartRows = useMemo(
    () => (timeSeries ? rows.slice(-12).map((r) => ({ ...r, __label: fmtPeriodLabel(r[labelKey as string]) })) : rows),
    [rows, timeSeries, labelKey]
  );

  if (!rows.length || !labelKey || !valueKeys.length) {
    return (
      <SectionCard title={title} subtitle={subtitle}>
        <Typography variant="caption" color="text.secondary">
          No data available.
        </Typography>
      </SectionCard>
    );
  }

  const primaryKey = valueKeys[0];
  const moneyPrimary = isMoneyKey(primaryKey);
  const yFmt = (v: number) => (moneyPrimary ? formatIndianNumber(v) : fmtInt(v));

  let chart: React.ReactNode;
  if (asDonut) {
    const filtered = rows.filter((r) => isNum(r[primaryKey]) && Number(r[primaryKey]) > 0);
    chart = filtered.length ? (
      <Box sx={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={filtered}
              dataKey={primaryKey}
              nameKey={labelKey}
              innerRadius={48}
              outerRadius={76}
              paddingAngle={2}
              label={(p: any) => `${p[labelKey]} (${p[primaryKey]})`}
              labelLine={false}
            >
              {filtered.map((_, i) => (
                <Cell key={i} fill={T.series(i)} />
              ))}
            </Pie>
            <RTooltip {...tooltipStyle(T)} />
            <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    ) : (
      <Typography variant="caption" color="text.secondary">No data available.</Typography>
    );
  } else if (timeSeries) {
    chart = (
      <Box sx={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          {valueKeys.length === 1 ? (
            <LineChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps(T)} />
              <XAxis dataKey="__label" {...axisProps(T)} minTickGap={20} />
              <YAxis {...axisProps(T)} width={60} tickFormatter={yFmt} allowDecimals={false} />
              <RTooltip {...tooltipStyle(T)} formatter={(v: any) => [yFmt(Number(v)), humanize(primaryKey)]} />
              <Line type="monotone" dataKey={primaryKey} stroke={T.series(0)} strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          ) : (
            <BarChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid {...gridProps(T)} />
              <XAxis dataKey="__label" {...axisProps(T)} minTickGap={20} />
              <YAxis {...axisProps(T)} width={60} tickFormatter={fmtInt} allowDecimals={false} />
              <RTooltip {...tooltipStyle(T)} formatter={(v: any, name: any) => [fmtInt(Number(v)), humanize(String(name))]} />
              <Legend wrapperStyle={{ fontSize: 11, color: T.textSecondary }} formatter={(v: string) => humanize(v)} />
              {valueKeys.map((vk, i) => (
                <Bar key={vk} dataKey={vk} name={vk} fill={T.series(i)} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </Box>
    );
  } else {
    chart = (
      <Box sx={{ height: Math.max(180, rows.length * 32) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
            <CartesianGrid {...gridProps(T)} horizontal={false} />
            <XAxis type="number" {...axisProps(T)} tickFormatter={yFmt} allowDecimals={false} />
            <YAxis type="category" dataKey={labelKey} {...axisProps(T)} width={110} tick={{ fill: T.textSecondary, fontSize: 11 }} />
            <RTooltip {...tooltipStyle(T)} formatter={(v: any) => [yFmt(Number(v)), humanize(primaryKey)]} />
            <Bar dataKey={primaryKey} radius={[0, 3, 3, 0]} maxBarSize={20}>
              {rows.map((_, i) => (
                <Cell key={i} fill={T.series(i)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  }

  const columns = [labelKey, ...valueKeys];

  return (
    <SectionCard title={title} subtitle={subtitle}>
      {chart}
      <Box sx={{ mt: 2, maxHeight: 220, overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c} sx={{ ...headerCellSx, py: 1 }}>
                  {humanize(c)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((r, i) => (
              <TableRow key={i} sx={bodyRowSx}>
                {columns.map((c) => (
                  <TableCell key={c} sx={{ py: 0.75, fontSize: '0.8125rem' }}>
                    {c === labelKey ? (timeSeries ? fmtPeriodLabel(r[c]) : r[c] ?? '—') : formatByKey(c, r[c])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </SectionCard>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const HrAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = () => {
    setLoading(true);
    setError(null);
    hrPlusService
      .analytics()
      .then((res: any) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load HR analytics'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <PageHeader
        title="HR Analytics"
        subtitle="Headcount, hiring, tenure, cost, attendance and recruitment trends across the workforce"
        actions={
          <IconButton onClick={fetchData} disabled={loading}>
            <Refresh />
          </IconButton>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !data ? (
        <TableEmptyState title="Could not load HR analytics" hint="Try refreshing." />
      ) : (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12 }}>
            <AnalyticsPanel title="Headcount Trend" subtitle="Total headcount over time" rows={data.headcountTrend || []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Hiring vs Attrition" subtitle="New joiners and exits over time" rows={data.hiringAttritionTrend || []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Attendance / Absenteeism" subtitle="Presence, absence and lateness over time" rows={data.attendanceTrend || []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Leave Utilization" subtitle="Leave taken vs entitlement over time" rows={data.leaveUtilization || []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Tenure Buckets" subtitle="Headcount by years of service" rows={data.tenureBuckets || []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Department Headcount & Cost" subtitle="Headcount and payroll cost by department" rows={data.departmentHeadcountCost || []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Recruitment Funnel" subtitle="Candidates by pipeline stage" rows={data.recruitmentFunnel || []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Gender Distribution" subtitle="Workforce split by gender" rows={data.genderDistribution || []} asDonut />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <AnalyticsPanel title="Employment Type Distribution" subtitle="Workforce split by employment type" rows={data.employmentTypeDistribution || []} asDonut />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HrAnalyticsPage;
